import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, KeyRound, Eye, EyeOff, Lock, Save, Pencil, Info, Calculator, TableProperties, Printer, Plus, Trash2, Minus } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatIDR } from "@/lib/constants";
import RupiahInput from "@/components/RupiahInput";

const EDITABLE_FIELDS = [
  { key: "base_salary", label: "Gaji Pokok" },
  { key: "allowance_meal", label: "Tunjangan Makan" },
  { key: "allowance_transport", label: "Tunjangan Transport" },
  { key: "allowance_other", label: "Tunjangan Lain-Lain" },
  { key: "bpjs_tk_amount", label: "BPJS TK", hint: "Isi 0 bila tidak ada potongan BPJS — angka di kolom adalah nilai final" },
  { key: "pph_amount", label: "PPh 21", hint: "Isi 0 bila tidak ada potongan PPh 21 — angka di kolom adalah nilai final" },
];

export default function SalaryDialog({ open, onOpenChange, targetUserId = null }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [openPphFormula, setOpenPphFormula] = useState(false);
  const isAdminView = !!targetUserId;

  // 149-cw-r171: gate disamakan dgn backend `can_admin_salary` — terima
  // `personnel_manage.update` ATAU `payroll_print.update` supaya tidak ada
  // mismatch "bisa ketik tapi save ditolak 403" (kasus akun eunike).
  const canEditSalary = !!user && (canPerm(user, "personnel_manage", "update") || canPerm(user, "payroll_print", "update"));

  const loadAdminSalary = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${targetUserId}/salary`);
      setSalary(data);
      setForm({
        base_salary: data.base_salary || 0,
        allowance_meal: data.allowance_meal || 0,
        allowance_transport: data.allowance_transport || 0,
        allowance_other: data.allowance_other || 0,
        // 149-cw-r172: auto-compute dihapus — seed langsung nilai tersimpan
        // (0 berarti memang tanpa potongan, bukan "auto").
        bpjs_tk_amount: data.bpjs_tk?.total || 0,
        pph_amount: data.pph_amount || 0,
        // 149-cw-r146: item tambahan / potongan manual — gabungkan dari 2 list.
        salary_adjustments: [
          ...((data.salary_adjustments?.additions) || []).map((it) => ({ ...it, kind: "addition" })),
          ...((data.salary_adjustments?.deductions) || []).map((it) => ({ ...it, kind: "deduction" })),
        ],
      });
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, [targetUserId]);

  useEffect(() => {
    if (!open) {
      setPassword(""); setSalary(null); setShowPw(false); setEditing(false); setForm({});
      return;
    }
    if (isAdminView) loadAdminSalary();
  }, [open, isAdminView, loadAdminSalary]);

  const verify = async () => {
    if (!password) { toast.error("Password wajib diisi"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/salary", { password });
      setSalary(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
    finally { setLoading(false); }
  };

  const saveEdit = async () => {
    if (!isAdminView) return;
    setSaving(true);
    try {
      const payload = {};
      EDITABLE_FIELDS.forEach((f) => {
        const raw = form[f.key];
        payload[f.key] = raw === "" || raw === null || raw === undefined ? 0 : Number(raw);
      });
      // 149-cw-r146: kirim salary_adjustments (filter yang invalid).
      payload.salary_adjustments = (form.salary_adjustments || [])
        .filter((it) => (it.label || "").trim() && Number(it.amount) > 0 && (it.kind === "addition" || it.kind === "deduction"))
        .map((it) => ({
          id: it.id,
          label: (it.label || "").trim(),
          amount: Number(it.amount),
          kind: it.kind,
        }));
      await api.patch(`/admin/users/${targetUserId}/salary`, payload);
      toast.success("Gaji personil disimpan");
      setEditing(false);
      await loadAdminSalary();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // 149-cw-r146: helpers utk manipulasi list salary_adjustments di form state.
  const addAdjustment = (kind) => {
    setForm((s) => ({
      ...s,
      salary_adjustments: [
        ...(s.salary_adjustments || []),
        { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label: "", amount: 0, kind },
      ],
    }));
  };
  const updateAdjustment = (idx, patch) => {
    setForm((s) => ({
      ...s,
      salary_adjustments: (s.salary_adjustments || []).map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };
  const removeAdjustment = (idx) => {
    setForm((s) => ({
      ...s,
      salary_adjustments: (s.salary_adjustments || []).filter((_, i) => i !== idx),
    }));
  };

  const Row = ({ label, value, strong, muted, warn }) => (
    <div className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0 text-xs">
      <div className={`${strong ? "font-semibold uppercase tracking-wider text-[10px]" : ""} ${muted ? "text-slate-500" : "text-slate-700"}`}>{label}</div>
      <div className={`tabular-nums ${strong ? "font-bold text-[#0B1D3A]" : ""} ${warn ? "text-rose-600" : ""}`}>{value}</div>
    </div>
  );

  const renderAdminEditForm = () => (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-300 p-2 text-[11px] text-amber-900">
        Anda sedang mengedit data gaji personil. Pastikan angka dalam rupiah penuh tanpa titik/koma.
      </div>
      <div className="grid grid-cols-1 gap-2">
        {EDITABLE_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</label>
            <div className="mt-1 flex items-center border border-slate-300 focus-within:border-[#0B1D3A] bg-white">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 px-2">Rp</span>
              <RupiahInput
                testid={`input-salary-${f.key}`}
                value={form[f.key] ?? 0}
                onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
                className="h-9 text-sm border-0"
              />
            </div>
            {f.hint && <div className="text-[9px] text-slate-400 mt-0.5">{f.hint}</div>}
          </div>
        ))}
      </div>

      {/* 149-cw-r146: Item Tambahan / Potongan Manual */}
      <div className="border border-slate-200 bg-slate-50" data-testid="salary-adjustments-editor">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
            Item Tambahan / Potongan Manual
          </div>
          <div className="text-[9px] text-slate-500 tracking-normal normal-case">Bonus, denda, kasbon, THR, dll.</div>
        </div>
        <div className="p-2 space-y-2">
          {(form.salary_adjustments || []).length === 0 && (
            <div className="text-center text-[10px] italic text-slate-400 py-2">
              Belum ada item. Tambahkan di bawah.
            </div>
          )}
          {(form.salary_adjustments || []).map((it, idx) => (
            <div key={it.id || idx} className="grid grid-cols-[80px_1fr_120px_28px] gap-1 items-center" data-testid={`adjustment-row-${idx}`}>
              <select
                value={it.kind}
                onChange={(e) => updateAdjustment(idx, { kind: e.target.value })}
                data-testid={`adjustment-kind-${idx}`}
                className={`h-8 text-[10px] font-semibold uppercase tracking-wider border rounded-none focus:outline-none px-1 ${
                  it.kind === "addition" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-rose-500 bg-rose-50 text-rose-700"
                }`}
              >
                <option value="addition">+ TAMBAH</option>
                <option value="deduction">− POTONG</option>
              </select>
              <input
                type="text"
                value={it.label}
                onChange={(e) => updateAdjustment(idx, { label: e.target.value })}
                maxLength={120}
                placeholder="Deskripsi (contoh: THR, Kasbon, Bonus proyek…)"
                data-testid={`adjustment-label-${idx}`}
                className="h-8 text-xs border border-slate-300 rounded-none focus:outline-none focus:border-[#0B1D3A] px-2"
              />
              <div className="flex items-center border border-slate-300 focus-within:border-[#0B1D3A] bg-white">
                <span className="text-[9px] uppercase tracking-wider text-slate-500 px-1">Rp</span>
                <RupiahInput
                  testid={`adjustment-amount-${idx}`}
                  value={it.amount ?? 0}
                  onChange={(v) => updateAdjustment(idx, { amount: v })}
                  className="h-8 text-xs border-0"
                />
              </div>
              <button
                type="button"
                onClick={() => removeAdjustment(idx)}
                data-testid={`adjustment-remove-${idx}`}
                className="h-8 w-7 inline-flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                aria-label="Hapus item"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-1 pt-1">
            <button
              type="button"
              onClick={() => addAdjustment("addition")}
              data-testid="adjustment-add-addition"
              className="flex-1 h-7 text-[10px] uppercase tracking-wider font-semibold border border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center justify-center gap-1"
            >
              <Plus size={11} /> Tambah Item (+)
            </button>
            <button
              type="button"
              onClick={() => addAdjustment("deduction")}
              data-testid="adjustment-add-deduction"
              className="flex-1 h-7 text-[10px] uppercase tracking-wider font-semibold border border-rose-500 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center justify-center gap-1"
            >
              <Minus size={11} /> Tambah Potongan (−)
            </button>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setEditing(false)} className="rounded-none">Batal</Button>
        <Button
          data-testid="btn-save-admin-salary"
          onClick={saveEdit}
          disabled={saving}
          className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
        >
          <Save size={14} className="mr-1" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md max-h-[92vh] overflow-y-auto" data-testid="dlg-salary">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Wallet size={20} className="text-[#FF5A1F]" /> {isAdminView ? (editing ? "Edit Salary Personil" : "Salary Personil") : "Salary Details"}
          </DialogTitle>
        </DialogHeader>

        {isAdminView && editing ? (
          renderAdminEditForm()
        ) : !salary ? (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 inline-flex items-start gap-2">
              <Lock size={14} className="shrink-0 mt-0.5" />
              <div>Untuk membuka informasi salary, masukkan password login Anda.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Password</label>
              <div className="relative mt-1">
                <Input
                  data-testid="input-salary-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  className="rounded-none h-9 text-sm pr-9"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="btn-toggle-pw"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Batal</Button>
              <Button
                data-testid="btn-verify-salary"
                onClick={verify}
                disabled={loading}
                className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
              >
                <KeyRound size={14} className="mr-1" /> {loading ? "Verifying…" : "Unlock"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border border-slate-200">
              <div className="px-3 py-2 border-b border-slate-200 uppercase text-[10px] tracking-wider font-semibold text-slate-500">Pendapatan</div>
              <div className="px-3 py-2" data-testid="salary-income">
                <Row label="Gaji Pokok" value={formatIDR(salary.base_salary)} />
                <Row label="Tunjangan Makan" value={formatIDR(salary.allowance_meal)} />
                <Row label="Tunjangan Transport" value={formatIDR(salary.allowance_transport)} />
                <Row label="Tunjangan Lain-Lain" value={formatIDR(salary.allowance_other)} />
                {/* 149-cw-r146: item tambahan manual */}
                {(salary.salary_adjustments?.additions || []).map((it) => (
                  <Row key={it.id} label={<span>+ {it.label}</span>} value={<span className="text-emerald-600">+ {formatIDR(it.amount)}</span>} />
                ))}
                <Row label="Gross" value={formatIDR(salary.gross)} strong />
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200">
              <div className="px-3 py-2 border-b border-slate-200 uppercase text-[10px] tracking-wider font-semibold text-slate-500">
                Potongan {salary.bpjs_tk.auto_computed && <span className="text-[9px] normal-case text-slate-400 tracking-normal">(BPJS auto-computed)</span>}
              </div>
              <div className="px-3 py-2" data-testid="salary-deductions">
                <Row label="BPJS TK — JHT (2%)" value={formatIDR(salary.bpjs_tk.jht_employee)} muted />
                <Row label="BPJS TK — JP (1%, cap 9.5jt)" value={formatIDR(salary.bpjs_tk.jp_employee)} muted />
                <Row label="Total BPJS TK" value={formatIDR(salary.bpjs_tk.total)} warn />
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0 text-xs">
                  <div className="text-slate-700 flex items-center gap-1">
                    Potongan PPh 21
                    {salary.pph_auto_computed && <span className="text-[9px] text-slate-400 normal-case">(auto)</span>}
                    <button
                      type="button"
                      data-testid="btn-open-pph-formula"
                      onClick={() => setOpenPphFormula(true)}
                      className="ml-1 text-[9px] uppercase tracking-wider text-[#FF5A1F] hover:underline inline-flex items-center gap-0.5"
                    >
                      <Info size={10} /> Rumus
                    </button>
                  </div>
                  <div className="tabular-nums text-rose-600">{formatIDR(salary.pph_amount)}</div>
                </div>
                {/* 149-cw-r146: item potongan manual */}
                {(salary.salary_adjustments?.deductions || []).map((it) => (
                  <Row key={it.id} label={<span>− {it.label}</span>} value={<span className="text-rose-600">− {formatIDR(it.amount)}</span>} />
                ))}
              </div>
            </div>

            <div className="bg-[#0B1D3A] text-white px-3 py-3">
              <div className="text-[10px] uppercase tracking-wider opacity-70">Estimasi Take Home Pay</div>
              <div className="font-display text-2xl tabular-nums text-[#FF5A1F] mt-0.5" data-testid="salary-take-home">
                {formatIDR(salary.take_home_pay)}
              </div>
            </div>

            <DialogFooter>
              {isAdminView && canEditSalary && (
                <Button
                  data-testid="btn-edit-admin-salary"
                  onClick={() => setEditing(true)}
                  variant="outline"
                  className="rounded-none border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white"
                >
                  <Pencil size={14} className="mr-1" /> Edit Gaji
                </Button>
              )}
              <Button
                data-testid="btn-print-slip"
                onClick={() => printSalarySlip(salary)}
                variant="outline"
                className="rounded-none border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white"
              >
                <Printer size={14} className="mr-1" /> Print Slip
              </Button>
              <Button
                data-testid="btn-close-salary"
                onClick={() => onOpenChange(false)}
                className="rounded-none bg-[#0B1D3A] hover:bg-[#132a5b] text-white flex-1"
              >
                Tutup
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      {/* PPh 21 Formula & Ketentuan Dialog */}
      <PphFormulaDialog open={openPphFormula} onOpenChange={setOpenPphFormula} salary={salary} />
    </Dialog>
  );
}

function PphFormulaDialog({ open, onOpenChange, salary }) {
  if (!salary) return null;
  const b = salary.pph_breakdown || {};
  const rowStyle = "flex justify-between items-baseline py-1 border-b border-slate-100 last:border-0 text-xs";
  const bracketRows = (b.brackets || []).map((br, i) => {
    const prev = i === 0 ? 0 : (b.brackets[i - 1].upper || 0);
    const upper = br.upper == null ? null : br.upper;
    const label = upper == null
      ? `> ${formatIDR(prev)}`
      : `${formatIDR(prev)} – ${formatIDR(upper)}`;
    return { label, rate: br.rate };
  });
  const ptkpEntries = Object.entries(b.ptkp_table || {}).sort();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dlg-pph-formula">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Calculator size={20} className="text-[#FF5A1F]" /> Rumus & Ketentuan PPh 21
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Perhitungan Personil Ini (PTKP {b.ptkp_status})</div>
            <div className="space-y-0" data-testid="pph-breakdown">
              <div className={rowStyle}><span className="text-slate-700">1. Gross bulanan × 12</span><span className="tabular-nums">{formatIDR(b.gross_annual)}</span></div>
              <div className={rowStyle}><span className="text-slate-700">2. − Biaya Jabatan (5%, maks 6.000.000/thn)</span><span className="tabular-nums text-rose-600">− {formatIDR(b.biaya_jabatan)}</span></div>
              <div className={rowStyle}><span className="text-slate-700">3. − Iuran BPJS TK karyawan (× 12)</span><span className="tabular-nums text-rose-600">− {formatIDR(b.bpjs_annual)}</span></div>
              <div className={rowStyle}><span className="font-semibold text-slate-800">= Penghasilan Neto Setahun</span><span className="tabular-nums font-semibold">{formatIDR(b.neto_annual)}</span></div>
              <div className={rowStyle}><span className="text-slate-700">4. − PTKP {b.ptkp_status}</span><span className="tabular-nums text-rose-600">− {formatIDR(b.ptkp_value)}</span></div>
              <div className={rowStyle}><span className="font-semibold text-slate-800">= PKP (dibulatkan ke bawah 1.000)</span><span className="tabular-nums font-semibold">{formatIDR(b.pkp)}</span></div>
              <div className={rowStyle}><span className="text-slate-700">5. Tarif progresif × PKP</span><span className="tabular-nums">{formatIDR(b.pph_annual)}</span></div>
              <div className="flex justify-between items-baseline py-2 mt-1 bg-[#0B1D3A] text-white px-2">
                <span className="text-[10px] uppercase tracking-wider">PPh 21 Bulanan (÷ 12)</span>
                <span className="font-display text-lg tabular-nums text-[#FF5A1F]" data-testid="pph-monthly-final">{formatIDR(b.pph_monthly)}</span>
              </div>
            </div>
            {salary.pph_auto_computed ? (
              <div className="text-[10px] text-emerald-700 mt-2">Nilai ini otomatis diterapkan pada slip gaji.</div>
            ) : (
              <div className="text-[10px] text-amber-700 mt-2">PPh 21 saat ini di-override manual ({formatIDR(salary.pph_amount)}). Set nilai override ke 0 untuk kembali ke perhitungan otomatis.</div>
            )}
          </div>

          <div className="border border-slate-200">
            <div className="px-3 py-2 border-b border-slate-200 uppercase text-[10px] tracking-wider font-semibold text-slate-500 flex items-center gap-1">
              <TableProperties size={12} /> Tarif Progresif (UU HPP 2022+)
            </div>
            <table className="w-full text-xs" data-testid="pph-bracket-table">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-1.5">Lapisan PKP Setahun</th>
                  <th className="text-right px-3 py-1.5">Tarif</th>
                </tr>
              </thead>
              <tbody>
                {bracketRows.map((br) => (
                  <tr key={br.label} className="border-t border-slate-100">
                    <td className="px-3 py-1.5">{br.label}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-[#0B1D3A]">{(br.rate * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-slate-200">
            <div className="px-3 py-2 border-b border-slate-200 uppercase text-[10px] tracking-wider font-semibold text-slate-500 flex items-center gap-1">
              <TableProperties size={12} /> Tabel PTKP (Setahun)
            </div>
            {(() => {
              const isFemale = (salary.gender || "").toUpperCase() === "P";
              return isFemale ? (
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
                  <span className="font-semibold uppercase tracking-wider text-[10px]">Aturan Khusus Karyawati (Gender P):</span>{" "}
                  Berdasarkan aturan PPh 21, tanggungan hanya diberikan kepada suami / kepala keluarga.
                  Karyawati <span className="font-semibold">SELALU</span> berstatus <span className="font-mono font-bold">TK/0</span> —
                  apapun status pernikahan &amp; berapapun jumlah anak. Baris status lain di bawah ditampilkan sebagai referensi saja (dicoret).
                </div>
              ) : null;
            })()}
            <table className="w-full text-xs" data-testid="ptkp-table">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="text-right px-3 py-1.5">Nilai</th>
                  <th className="text-left px-3 py-1.5">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {ptkpEntries.map(([k, v]) => {
                  const isActive = k === b.ptkp_status;
                  const isFemale = (salary.gender || "").toUpperCase() === "P";
                  const isOnlyRow = isFemale && k === "TK/0";
                  const isMuted = isFemale && k !== "TK/0";
                  let ket;
                  if (k.startsWith("K/")) {
                    ket = isFemale
                      ? `Menikah, ${k.slice(2)} tanggungan — tidak berlaku untuk karyawati`
                      : `Menikah, ${k.slice(2)} tanggungan`;
                  } else {
                    ket = isFemale && k === "TK/0"
                      ? `Berlaku untuk semua karyawati (apapun status pernikahan / jumlah anak)`
                      : `Belum Menikah, ${k.slice(3)} tanggungan`;
                  }
                  return (
                    <tr key={k} className={`border-t border-slate-100 ${isActive ? "bg-amber-50" : ""} ${isOnlyRow ? "bg-emerald-50" : ""} ${isMuted ? "text-slate-400" : ""}`}>
                      <td className={`px-3 py-1.5 font-semibold ${isMuted ? "line-through decoration-slate-400" : ""}`}>
                        {k}
                        {isActive && <span className="ml-1 text-[9px] text-amber-700">← aktif</span>}
                        {isOnlyRow && !isActive && <span className="ml-1 text-[9px] text-emerald-700 font-normal">← wajib untuk karyawati</span>}
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${isMuted ? "line-through decoration-slate-400" : ""}`}>{formatIDR(v)}</td>
                      <td className="px-3 py-1.5 text-slate-500">{ket}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[10px] text-slate-500 italic">
            Referensi: UU No. 7/2021 (HPP) &amp; PMK-168/2023. Perhitungan asumsi masa kerja penuh 12 bulan; koreksi akhir tahun dilakukan HR bila terjadi mutasi/PHK.
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="rounded-none bg-[#0B1D3A] hover:bg-[#132a5b] text-white">Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Buka window baru dengan slip gaji siap-print (Save as PDF via browser).
 * Header perusahaan: PT. Eka Nusa Sinergi (ENS).
 */
function printSalarySlip(salary) {
  if (!salary) return;
  const idr = (v) => "Rp " + (Number(v) || 0).toLocaleString("id-ID");
  const today = new Date();
  const period = today.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const printedAt = today.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) + " " + today.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  const b = salary.pph_breakdown || {};
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Slip Gaji · ${salary.employee_name || ""}</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #0B1D3A; margin: 0; padding: 0; }
  .company { border-bottom: 3px solid #0B1D3A; padding-bottom: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; }
  .company .brand { display: flex; align-items: center; gap: 12px; }
  .company .logo { width: 44px; height: 44px; background: #0B1D3A; color: #FF5A1F; font-weight: 900; font-size: 20px; display: flex; align-items: center; justify-content: center; letter-spacing: -1px; }
  .company .name { font-size: 16px; font-weight: 700; letter-spacing: -0.5px; }
  .company .tag { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1.5px; }
  .company .doc-info { text-align: right; font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .doc-info strong { display: block; color: #0B1D3A; font-size: 14px; letter-spacing: -0.3px; margin-bottom: 2px; text-transform: none; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-bottom: 16px; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; }
  .meta .cell { font-size: 10px; }
  .meta .cell .k { font-size: 8.5px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .meta .cell .v { font-size: 11px; color: #0B1D3A; font-weight: 600; margin-top: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table th { text-align: left; background: #0B1D3A; color: #fff; padding: 6px 10px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
  table td { padding: 5px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
  table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table tr.total td { font-weight: 700; border-top: 2px solid #0B1D3A; border-bottom: none; background: #f1f5f9; }
  .th-pay { background: #10b981; }
  .th-ded { background: #f43f5e; }
  .thp { margin-top: 6px; background: #0B1D3A; color: #fff; padding: 12px 14px; display: flex; justify-content: space-between; align-items: center; }
  .thp .k { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.85; }
  .thp .v { font-size: 22px; font-weight: 800; color: #FF5A1F; font-variant-numeric: tabular-nums; letter-spacing: -0.5px; }
  .foot { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 40px; font-size: 10px; }
  .foot .sig { text-align: center; }
  .foot .sig .line { border-top: 1px solid #0B1D3A; margin: 60px 0 4px; }
  .foot .sig .role { color: #64748b; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; }
  .notice { margin-top: 24px; padding: 8px 10px; background: #fef3c7; border-left: 3px solid #f59e0b; font-size: 9px; color: #78350f; line-height: 1.5; }
  .footer-note { margin-top: 28px; padding-top: 8px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 8.5px; color: #94a3b8; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  <div class="company">
    <div class="brand">
      <div class="logo">ENS</div>
      <div>
        <div class="name">PT. EKA NUSA SINERGI</div>
        <div class="tag">Contractor · Construction · Development</div>
      </div>
    </div>
    <div class="doc-info">
      <strong>SLIP GAJI</strong>
      <div>Periode: ${period}</div>
      <div>Dicetak: ${printedAt}</div>
    </div>
  </div>
  <div class="meta">
    <div class="cell"><div class="k">Nama Karyawan</div><div class="v">${salary.employee_name || "-"}</div></div>
    <div class="cell"><div class="k">Divisi / Sub-Divisi</div><div class="v">${salary.employee_division || "-"} · ${salary.employee_sub_division || "-"}</div></div>
    <div class="cell"><div class="k">Jabatan / Posisi</div><div class="v">${salary.employee_position || "-"}</div></div>
    <div class="cell"><div class="k">Status PTKP</div><div class="v">${salary.employee_ptkp_status || b.ptkp_status || "-"}</div></div>
  </div>
  <table>
    <thead><tr><th class="th-pay">Pendapatan (Earnings)</th><th class="th-pay num" style="width: 30%">Jumlah (IDR)</th></tr></thead>
    <tbody>
      <tr><td>Gaji Pokok</td><td class="num">${idr(salary.base_salary)}</td></tr>
      <tr><td>Tunjangan Makan</td><td class="num">${idr(salary.allowance_meal)}</td></tr>
      <tr><td>Tunjangan Transport</td><td class="num">${idr(salary.allowance_transport)}</td></tr>
      <tr><td>Tunjangan Lain-Lain</td><td class="num">${idr(salary.allowance_other)}</td></tr>
      <tr class="total"><td>Total Pendapatan (Gross)</td><td class="num">${idr(salary.gross)}</td></tr>
    </tbody>
  </table>
  <table>
    <thead><tr><th class="th-ded">Potongan (Deductions)</th><th class="th-ded num" style="width: 30%">Jumlah (IDR)</th></tr></thead>
    <tbody>
      <tr><td>BPJS Ketenagakerjaan — JHT (2%)</td><td class="num">${idr(salary.bpjs_tk.jht_employee)}</td></tr>
      <tr><td>BPJS Ketenagakerjaan — JP (1%, cap 9,5jt)</td><td class="num">${idr(salary.bpjs_tk.jp_employee)}</td></tr>
      <tr><td>PPh 21 (${salary.pph_auto_computed ? "otomatis, PTKP " + (b.ptkp_status || "-") : "manual override"})</td><td class="num">${idr(salary.pph_amount)}</td></tr>
      <tr class="total"><td>Total Potongan</td><td class="num">${idr((salary.bpjs_tk.total || 0) + (salary.pph_amount || 0))}</td></tr>
    </tbody>
  </table>
  <div class="thp">
    <div class="k">Take Home Pay</div>
    <div class="v">${idr(salary.take_home_pay)}</div>
  </div>
  <div class="foot">
    <div class="sig">
      <div class="line"></div>
      <div class="role">Karyawan</div>
      <div style="margin-top:2px; font-weight:600;">${salary.employee_name || ""}</div>
    </div>
    <div class="sig">
      <div class="line"></div>
      <div class="role">HR / Finance</div>
      <div style="margin-top:2px; font-weight:600;">PT. Eka Nusa Sinergi</div>
    </div>
  </div>
  <div class="notice"><strong>Catatan:</strong> Slip gaji ini merupakan dokumen internal PT. Eka Nusa Sinergi. Perhitungan PPh 21 mengikuti UU No. 7/2021 (HPP) &amp; PMK-168/2023 dengan tarif progresif. BPJS Ketenagakerjaan dihitung dari komponen gaji pokok. Untuk pertanyaan terkait perhitungan, hubungi HR / Finance.</div>
  <div class="footer-note">Generated by ENS Task Manager · Dokumen konfidensial — jangan disebarluaskan tanpa izin</div>
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 250); };</script>
</body></html>`;
  // XSS-safe: pakai Blob + object URL (bukan document.write) supaya CSP-friendly.
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=800,height=900");
  if (!w) { URL.revokeObjectURL(url); alert("Popup diblokir browser. Izinkan popup untuk print slip."); return; }
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
